"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.processErrorCodeErrors = exports.processFieldErrors = exports.handleErrorResponse = exports.extractResponseData = void 0;
// utils/apiHelpers.ts
const constant_1 = require("../constants/constant");
/**
 * Asynchronously extracts data from a fetch response and handles success and error scenarios.
 *
 * @template T The type of the data in a successful response.
 *
 * @param {Response} response - The fetch response to extract data from.
 * @param {<T>(data: T) => void} successCallback - A callback function that is called when the response is successful. The data from the response is passed to this callback.
 * @param {ErrorMappings} errorMappings - Mappings of field names or error codes to error state setters. Used to set error states based on the errors in the response.
 * else callback a generic error message if an error occurs and no specific error mapping is provided for the error.
 *
 * @returns {Promise<void>} A Promise that resolves to void.
 *
 */
const extractResponseData = async (response, successCallback, errorMappings) => {
    if (response.ok) {
        // assumption: API will always have some sort of data in the response
        const successResponse = await response.json();
        successCallback(successResponse);
    }
    else if (errorMappings) {
        const errorData = await response.json();
        (0, exports.handleErrorResponse)(errorData, errorMappings);
    }
    else {
        throw new Error(constant_1.SOMETHING_WENT_WRONG_ERROR);
    }
};
exports.extractResponseData = extractResponseData;
/**
 * Handles the error response by resetting state setters, processing field errors and error code errors,
 * and handling general errors if no specific errors are found.
 * An error that has both a field and a mapped error code is handled by its error code callback only.
 *
 * @param data - The error response data.
 * @param errorMappings - The error mappings for processing the errors.
 */
const handleErrorResponse = (data, errorMappings) => {
    resetvalidationErrorsSetters(errorMappings);
    const hasMappedErrorCode = (error) => Boolean(error.Field && error.ErrorCode && errorMappings.errorcode?.[error.ErrorCode]);
    const errorCodeFirstData = { Errors: data.Errors.filter(hasMappedErrorCode) };
    const remainingData = { Errors: data.Errors.filter((error) => !hasMappedErrorCode(error)) };
    const hasErrorCodeFirstError = (0, exports.processErrorCodeErrors)(errorCodeFirstData, errorMappings);
    const hasRemainingError = (0, exports.processFieldErrors)(remainingData, errorMappings) || (0, exports.processErrorCodeErrors)(remainingData, errorMappings);
    if (!hasErrorCodeFirstError && !hasRemainingError) {
        handleGeneralError(errorMappings);
    }
};
exports.handleErrorResponse = handleErrorResponse;
/**
 * Resets all state setters by invoking them with an empty string.
 * @param errorMappings - The error mappings object.
 */
const resetvalidationErrorsSetters = (errorMappings) => {
    errorMappings.validationErrorsSetters?.forEach((setter) => setter(''));
};
/**
 * Processes field errors from the provided ErrorResponse object and updates the state of the corresponding fields using the provided errorMappings.
 * @param data - The ErrorResponse object containing the error data.
 * @param errorMappings - The ErrorMappings object containing the mapping between field names and state setters.
 * @returns True if there are any field errors, false otherwise.
 */
const processFieldErrors = (data, errorMappings) => {
    let hasError = false;
    data.Errors.forEach((error) => {
        if (error.Field) {
            const errorMapping = errorMappings.fields[error.Field];
            if (errorMapping) {
                errorMapping.stateSetter(error.Message);
                hasError = true;
            }
        }
    });
    return hasError;
};
exports.processFieldErrors = processFieldErrors;
/**
 * Processes the error codes in the given data and invokes the corresponding callback functions based on the error mappings.
 * @param data - The error response data.
 * @param errorMappings - The error mappings object containing the callback functions for each error code.
 * @returns True if there is at least one error code that matches the error mappings, false otherwise.
 */
const processErrorCodeErrors = (data, errorMappings) => {
    let hasError = false;
    if (errorMappings.errorcode) {
        data.Errors.forEach((error) => {
            if (error.ErrorCode) {
                const codeMapping = errorMappings.errorcode[error.ErrorCode];
                if (codeMapping) {
                    codeMapping.callback(error.Message);
                    hasError = true;
                }
            }
        });
    }
    return hasError;
};
exports.processErrorCodeErrors = processErrorCodeErrors;
/**
 * Handles general errors based on the provided error mappings.
 * @param errorMappings - The error mappings object.
 */
const handleGeneralError = (errorMappings) => {
    errorMappings?.errorcode?.general?.callback(constant_1.SOMETHING_WENT_WRONG_ERROR);
};
